import { ApiResponse, TicketManagerMember, TicketManagerPool } from '@vision/ui/interfaces';
import { createHttpExtraOptions } from '../../../utils';
import { TicketManagerApi } from '../ticket-manager.api';
import { normalizeTicketManagerMembersResponse } from '../members';
import {
  TicketManagerPoolCreateArgs,
  TicketManagerPoolDeleteArgs,
  TicketManagerPoolMemberAddArgs,
  TicketManagerPoolMemberRemoveArgs,
  TicketManagerPoolMembersArgs,
  TicketManagerPoolUpdateArgs,
  TicketManagerProjectPoolsArgs,
  TicketManagerTicketsExportArgs,
} from './pools.api.types';
import { normalizeTicketManagerPoolResponse, normalizeTicketManagerPoolsResponse } from './pools.mappers';
import {
  buildAddPoolMemberRequest,
  buildCreatePoolRequest,
  buildDeletePoolRequest,
  buildExportPoolTicketsRequest,
  buildGetPoolMembersRequest,
  buildGetProjectPoolsRequest,
  buildRemovePoolMemberRequest,
  buildUpdatePoolRequest,
} from './pools.routes';

export const PoolEndpoints = TicketManagerApi.injectEndpoints({
  endpoints: (builder) => ({
    deleteTicketManagerPool: builder.mutation<void, TicketManagerPoolDeleteArgs>({
      query: (args) => ({
        ...buildDeletePoolRequest(args),
        extraOptions: createHttpExtraOptions({
          notification: { autoHandleError: false, autoHandleSuccess: false },
        }),
      }),
      invalidatesTags: (_result, error, { projectId }) =>
        error
          ? []
          : [
              { type: 'TicketManagerProjectPools', id: projectId },
              { type: 'TicketManagerProjectList', id: 'LIST' },
            ],
    }),

    ticketManagerProjectPools: builder.query<ApiResponse<TicketManagerPool[]>, TicketManagerProjectPoolsArgs>({
      query: ({ projectId }) => buildGetProjectPoolsRequest({ projectId }),
      transformResponse: normalizeTicketManagerPoolsResponse,
      providesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectPools', id: projectId }],
    }),

    createTicketManagerPool: builder.mutation<ApiResponse<TicketManagerPool>, TicketManagerPoolCreateArgs>({
      query: ({ name, nodeId, projectId }) => buildCreatePoolRequest({ name, nodeId, projectId }),
      transformResponse: normalizeTicketManagerPoolResponse,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectPools', id: projectId }],
    }),

    updateTicketManagerPool: builder.mutation<ApiResponse<TicketManagerPool>, TicketManagerPoolUpdateArgs>({
      query: ({ name, nodeId, poolId, projectId }) => buildUpdatePoolRequest({ name, nodeId, poolId, projectId }),
      transformResponse: normalizeTicketManagerPoolResponse,
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'TicketManagerProjectPools', id: projectId }],
    }),

    ticketManagerPoolMembers: builder.query<ApiResponse<TicketManagerMember[]>, TicketManagerPoolMembersArgs>({
      query: ({ poolId, projectId }) => buildGetPoolMembersRequest({ poolId, projectId }),
      transformResponse: normalizeTicketManagerMembersResponse,
      providesTags: (_result, _error, { poolId }) => [{ type: 'TicketManagerPoolMembers', id: poolId }],
    }),

    addTicketManagerPoolMember: builder.mutation<Record<string, never>, TicketManagerPoolMemberAddArgs>({
      query: ({ poolId, projectId, role, userId }) => buildAddPoolMemberRequest({ poolId, projectId, role, userId }),
      invalidatesTags: (_result, _error, { poolId, projectId }) => [
        { type: 'TicketManagerPoolMembers', id: poolId },
        { type: 'TicketManagerProjectPools', id: projectId },
      ],
    }),

    removeTicketManagerPoolMember: builder.mutation<Record<string, never>, TicketManagerPoolMemberRemoveArgs>({
      query: ({ poolId, projectId, userId }) => buildRemovePoolMemberRequest({ poolId, projectId, userId }),
      invalidatesTags: (_result, _error, { poolId, projectId }) => [
        { type: 'TicketManagerPoolMembers', id: poolId },
        { type: 'TicketManagerProjectPools', id: projectId },
      ],
    }),

    exportTicketManagerTickets: builder.mutation<Record<string, never>, TicketManagerTicketsExportArgs>({
      query: (args) => ({
        ...buildExportPoolTicketsRequest(args),
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.ticketManager.exportTickets.{{status}}',
          },
        }),
      }),
    }),
  }),
});

export const {
  useDeleteTicketManagerPoolMutation,
  useTicketManagerProjectPoolsQuery,
  useLazyTicketManagerProjectPoolsQuery,
  useCreateTicketManagerPoolMutation,
  useUpdateTicketManagerPoolMutation,
  useTicketManagerPoolMembersQuery,
  useAddTicketManagerPoolMemberMutation,
  useRemoveTicketManagerPoolMemberMutation,
  useExportTicketManagerTicketsMutation,
} = PoolEndpoints;
